import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "../extensions/L.Control.MapCenterCoord.min.js";
import "../extensions/L.Control.MapCenterCoord.min.css";
import { eventBus } from "@/main";
import store from "@/store";
import { useMomentaryStore } from "@/stores/momentary";

// Расширяем L.TileLayer для поддержки токенов и кэширования
L.TileLayer.FetchTokenLayer = L.TileLayer.extend({
  initialize: function (url, options) {
    this._token = options.token;
    this._requests = {};
    this._cache = {};
    this._cacheSize = options.cacheSize || 100;
    this._timeout = options.timeout || 10000;
    L.TileLayer.prototype.initialize.call(this, url, options);
  },

  onRemove: function (map) {
    // Отменяем все активные запросы
    Object.values(this._requests).forEach((controller) => {
      try {
        controller.abort();
      } catch (e) {
        // Игнорируем ошибки при отмене запросов
      }
    });

    // Освобождаем все URL объекты из кэша
    Object.values(this._cache).forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch (e) {
        // Игнорируем ошибки при освобождении URL
      }
    });

    // Очищаем все ссылки
    this._requests = {};
    this._cache = {};
    this._token = null;

    // Вызываем родительский onRemove для корректной очистки DOM
    // Может вызвать ошибку parentNode, но это не критично для функциональности
    L.TileLayer.prototype.onRemove.call(this, map);
  },

  createTile: function (coords, done) {
    const tile = document.createElement("img");
    tile.alt = "";
    tile.setAttribute("role", "presentation");

    const tileId = this._tileCoordsToKey(coords);
    const url = this.getTileUrl(coords);

    // Используем кэш, если доступен
    if (this._cache[url]) {
      tile.src = this._cache[url];
      setTimeout(() => done(null, tile), 0);
      return tile;
    }

    // Отменяем предыдущий запрос для тайла
    if (this._requests[tileId]) {
      this._requests[tileId].abort();
    }

    const controller = new AbortController();
    this._requests[tileId] = controller;

    tile.onload = () => {
      done(null, tile);
      delete this._requests[tileId];
    };

    tile.onerror = (err) => {
      done(err, tile);
      delete this._requests[tileId];
    };

    const timeoutId = setTimeout(() => {
      controller.abort();
      done(new Error("Tile request timeout"), tile);
      delete this._requests[tileId];
    }, this._timeout);

    fetch(url, {
      headers: { Authorization: "Bearer " + this._token },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP error ${response.status}`);
        return response.blob();
      })
      .then((blob) => {
        clearTimeout(timeoutId);
        const objectUrl = URL.createObjectURL(blob);

        // Управляем размером кэша, удаляя старые элементы
        if (this._cache) {
          const cacheKeys = Object.keys(this._cache);
          if (cacheKeys.length >= this._cacheSize) {
            const oldUrl = this._cache[cacheKeys[0]];
            if (oldUrl) {
              try {
                URL.revokeObjectURL(oldUrl);
              } catch (e) {
                // Игнорируем ошибки освобождения старых URL
              }
            }
            delete this._cache[cacheKeys[0]];
          }

          this._cache[url] = objectUrl;
        }

        const cacheKeys = Object.keys(this._cache);
        if (cacheKeys.length >= this._cacheSize) {
          URL.revokeObjectURL(this._cache[cacheKeys[0]]);
          delete this._cache[cacheKeys[0]];
        }

        this._cache[url] = objectUrl;
        tile.src = objectUrl;
      })
      .catch((err) => {
        clearTimeout(timeoutId);
        if (!controller.signal.aborted) {
          console.error("Tile fetch error:", err);
          done(err, tile);
        }
      });

    return tile;
  },
});

L.tileLayer.fetchTokenLayer = function (url, options) {
  return new L.TileLayer.FetchTokenLayer(url, options);
};

// Базовый класс для Leaflet карт
export default class BaseLeaf {
  constructor(elementId, options = {}) {
    this.options = {
      center: [60.1699, 24.9384], // Helsinki по умолчанию
      zoom: 10,
      minZoom: 2,
      maxZoom: 20,
      ...options,
    };
    this.elementId = elementId;
    this.map = null;
    this.currentLayer = null;
    this.layers = {};
    this.layerControl = null;
    this.overlays = {};
    this.momentaryStore = null; // Инициализируется в init()

    // Инициализируем только если передан элемент
    if (elementId) {
      this.init(elementId);
    }
  }

  // Инициализация карты
  init(el, tiles) {
    this.enabledTiles = tiles || ["osm"];

    try {
      if (!el) throw new Error("Map container element not provided");

      // Инициализируем хранилище momentary
      this.momentaryStore = useMomentaryStore();

      // Проверяем, есть ли сохраненное состояние карты
      const savedMapState = this.momentaryStore.getMapState();

      this.options.center = [savedMapState.lat, savedMapState.lon];
      this.options.zoom = savedMapState.zoom;

      const mapOptions = {
        center: this.options.center,
        zoom: this.options.zoom,
        minZoom: this.options.minZoom,
        maxZoom: this.options.maxZoom,
        attributionControl: false,
      };

      this.map = L.map(el, mapOptions);

      // Добавляем контролы
      L.control
        .mapCenterCoord({ position: "bottomright", latlngFormat: "DD" })
        .addTo(this.map);
      L.control
        .mapCenterCoord({
          position: "bottomleft",
          latlngFormat: "DM",
          latlngDesignators: true,
        })
        .addTo(this.map);
      L.control
        .attribution({
          position: "topright",
          prefix: false,
        })
        .addTo(this.map);

      // Инициализируем базовые слои и контролы
      this.createPanes();
      this.initTileLayers();
      this.addLayerControl();
      this.hideAttributionFlag();

      // Добавляем обработчики событий для сохранения состояния карты
      this.setupMapStateListeners();

      eventBus.$emit("map-initialized", this.map);
    } catch (error) {
      console.error("Failed to initialize map:", error);
      eventBus.$emit("map-initialization-failed", error);
    }
  }

  // Скрываем флаг копирайта Leaflet
  hideAttributionFlag() {
    const style = document.createElement("style");
    style.textContent =
      ".leaflet-attribution-flag { display: none !important; }";
    document.head.appendChild(style);

    setTimeout(() => {
      document
        .querySelectorAll(".leaflet-attribution-flag")
        .forEach((flag) => flag.remove());
    }, 100);
  }

  // Настройка слушателей для сохранения состояния карты
  setupMapStateListeners() {
    if (!this.map || !this.momentaryStore) return;

    // Сохраняем состояние карты при изменении центра или зума
    this.map.on("moveend zoomend", () => {
      const center = this.map.getCenter();
      const zoom = this.map.getZoom();
      this.momentaryStore.saveMapState(center.lat, center.lng, zoom);
    });
  }

  // Настройка панелей карты
  createPanes() {
    const panes = {
      tiles: 200,
      maritimeTiles: 250,
      weatherTiles: 250,
      overlays: 400,
      aio: 420,
      ports: 450,
      navarea: 500,
      markers: 600,
      popups: 700,
    };

    Object.entries(panes).forEach(([name, zIndex]) => {
      this.map.createPane(name);
      this.map.getPane(name).style.zIndex = zIndex;
    });
  }

  // Инициализируем базовые слои карты
  initTileLayers() {
    const commonOptions = {
      minZoom: this.options.minZoom,
      maxZoom: this.options.maxZoom,
      pane: "tiles",
    };

    // Базовые слои
    if (this.enabledTiles.includes("osm")) {
      this.layers.osm = L.tileLayer(
        "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
        {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          ...commonOptions,
        }
      );
    }

    if (this.enabledTiles.includes("google")) {
      this.layers.googleEarth = L.tileLayer(
        "https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}",
        {
          subdomains: ["mt0", "mt1", "mt2", "mt3"],
          attribution: "&copy; Google Maps",
          ...commonOptions,
        }
      );
    }

    if (this.enabledTiles.includes("amap")) {
      const amapSat = L.tileLayer(
        "https://webst0{s}.is.autonavi.com/appmaptile?style=6&x={x}&y={y}&z={z}",
        {
          subdomains: ["1", "2", "3", "4"],
          maxNativeZoom: 18,
          maxZoom: this.options.maxZoom,
          ...commonOptions,
        }
      );

      this.layers.amap = L.layerGroup([amapSat]);
    }

    const currentUnixTime = Math.floor(Date.now() / 1000);
    const deviceId = 0;
    const draught = 10;

    // Морские слои
    if (this.enabledTiles.includes("primar")) {
      const osmForMaritime = L.tileLayer(
        "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
        {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          ...commonOptions,
        }
      );

      const maritimeLayer = L.tileLayer.fetchTokenLayer(
        `https://mwend.bgeo.fi/api/v1/nogotile/{z}/{x}/{y}/?depth=${draught}&deviceId=${deviceId}`,
        {
          token: store.state.auth.user.token,
          attribution: "&copy; Primar",
          minZoom: 4,
          maxZoom: 20,
          opacity: 1,
          pane: "maritimeTiles",
        }
      );
      this.layers.maritime = L.layerGroup([osmForMaritime, maritimeLayer]);
    }

    // Погодные слои
    if (this.enabledTiles.includes("weather")) {
      const osmForWeather = L.tileLayer(
        "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
        {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          ...commonOptions,
        }
      );

      const weatherLayer = L.tileLayer.fetchTokenLayer(
        `https://nogotiles.bgeo.fi:6011/wind/{z}/{x}/{y}?t=${currentUnixTime}`,
        {
          token: store.state.auth.user.token,
          attribution: "&copy; NOAA, &copy; Primar",
          minZoom: 4,
          maxZoom: 20,
          opacity: 0.7,
          pane: "weatherTiles",
        }
      );
      this.layers.weather = L.layerGroup([osmForWeather, weatherLayer]);
    }

    // В Китае предпочитаем AMap, так как он работает намного быстрее и стабильнее
    const isChina = navigator.language.startsWith('zh');
    
    if (isChina && this.enabledTiles.includes("amap")) {
      this.setActiveLayer("amap");
    } else if (this.enabledTiles.includes("osm")) {
      this.setActiveLayer("osm");
    }
  }

  // Добавляем контрол слоев
  addLayerControl() {
    const baseLayers = {};

    if (this.layers.osm && this.enabledTiles.includes("osm")) {
      baseLayers["OpenStreetMaps"] = this.layers.osm;
    }

    if (this.layers.googleEarth && this.enabledTiles.includes("google")) {
      baseLayers["Google Earth"] = this.layers.googleEarth;
    }

    if (this.layers.amap && this.enabledTiles.includes("amap")) {
      baseLayers["AMap"] = this.layers.amap;
    }

    if (this.layers.maritime && this.enabledTiles.includes("primar")) {
      baseLayers["WMF"] = this.layers.maritime;
    }

    if (this.layers.weather && this.enabledTiles.includes("weather")) {
      baseLayers["Weather"] = this.layers.weather;
    }

    if (Object.keys(baseLayers).length > 0) {
      this.layerControl = L.control.layers(baseLayers).addTo(this.map);
    }
  }

  // Устанавливаем активный слой по ID
  setActiveLayer(layerId) {
    if (this.map) {
      // Remove current layers
      Object.values(this.layers).forEach((layer) => {
        if (this.map.hasLayer(layer)) {
          this.map.removeLayer(layer);
        }
      });

      if (this.layers[layerId]) {
        this.currentLayer = this.layers[layerId];
        this.currentLayer.addTo(this.map);
        return true;
      }
    }
    return false;
  }

  // Удаляем карту и все слои
  destroy() {
    if (!this.map) return;

    try {
      // Отключаем все event listeners
      this.map.off();

      // Принудительно очищаем все слои и их ресурсы
      Object.values(this.layers).forEach((layer) => {
        if (this.map.hasLayer(layer)) {
          this.map.removeLayer(layer);
        }

        // Если это слой с кэшем (FetchTokenLayer), очищаем его
        if (layer instanceof L.TileLayer.FetchTokenLayer) {
          layer.onRemove(this.map);
        }

        // Если это layerGroup, очищаем все внутренние слои
        if (layer instanceof L.LayerGroup) {
          layer.eachLayer((sublayer) => {
            if (sublayer instanceof L.TileLayer.FetchTokenLayer) {
              sublayer.onRemove(this.map);
            }
          });
          layer.clearLayers();
        }
      });

      // Очищаем контрол слоев
      if (this.layerControl) {
        this.map.removeControl(this.layerControl);
        this.layerControl = null;
      }

      // Очищаем все контролы
      this.map.eachLayer((layer) => {
        this.map.removeLayer(layer);
      });

      // Очищаем все панели
      const mapContainer = this.map.getContainer();

      // Удаляем все CSS стили, добавленные для attribution
      const attributionStyles = document.querySelectorAll("style");
      attributionStyles.forEach((style) => {
        if (style.textContent.includes("leaflet-attribution-flag")) {
          style.remove();
        }
      });

      // Освобождаем ссылки
      this.layers = {};
      this.overlays = {};
      this.currentLayer = null;
      this.momentaryStore = null;

      // Удаляем карту
      this.map.remove();
      this.map = null;

      // Принудительно очищаем DOM контейнер
      if (mapContainer) {
        mapContainer.innerHTML = "";
      }
    } catch (error) {
      console.warn("Ошибка при очистке карты:", error);
      // В случае ошибки все равно обнуляем ссылки
      this.map = null;
      this.layers = {};
      this.overlays = {};
      this.currentLayer = null;
      this.momentaryStore = null;
    }
  }
}
